/**
 * DrawableMap - Map with polygon drawing for area search
 * Similar to Rightmove's "Draw a search" feature
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, FeatureGroup, Polygon, useMap, Marker, Popup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  PenTool, Trash2, Undo2, MapPin, X, 
  List, Maximize2, Minimize2, Search
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom cluster icon
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = count >= 10 ? 52 : count >= 5 ? 44 : 36;
  
  return L.divIcon({
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      color: white;
      font-weight: 700;
      font-size: ${size > 44 ? '16px' : '12px'};
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
      border: 3px solid white;
    ">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

// Property marker icon
const createPropertyIcon = (price, currency) => {
  const formattedPrice = price >= 1000 
    ? `${currency}${(price / 1000).toFixed(0)}k` 
    : `${currency}${price}`;
  
  return L.divIcon({
    className: 'custom-property-marker',
    html: `<div style="
      background: #ffffff;
      color: #1f2937;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      border: 2px solid #e5e7eb;
    ">${formattedPrice}</div>`,
    iconSize: [60, 30],
    iconAnchor: [30, 30],
  });
};

// Check if a point is inside a polygon
const isPointInPolygon = (point, polygon) => {
  const x = point[0], y = point[1];
  let inside = false;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  
  return inside;
};

// Map controller for drawing
const DrawController = ({ onDrawCreated, onDrawDeleted, onDrawEdited }) => {
  return (
    <FeatureGroup>
      <EditControl
        position="topleft"
        onCreated={onDrawCreated}
        onDeleted={onDrawDeleted}
        onEdited={onDrawEdited}
        draw={{
          rectangle: false,
          circle: false,
          circlemarker: false,
          marker: false,
          polyline: false,
          polygon: {
            allowIntersection: false,
            drawError: {
              color: '#e74c3c',
              message: 'Lines cannot cross!'
            },
            shapeOptions: {
              color: '#3b82f6',
              fillColor: '#3b82f6',
              fillOpacity: 0.2,
              weight: 3
            },
            icon: new L.DivIcon({
              iconSize: new L.Point(10, 10),
              className: 'leaflet-div-icon leaflet-editing-icon'
            }),
            touchIcon: new L.DivIcon({
              iconSize: new L.Point(16, 16),
              className: 'leaflet-div-icon leaflet-editing-icon leaflet-touch-icon'
            })
          }
        }}
        edit={{
          edit: {
            selectedPathOptions: {
              dashArray: '10, 10',
              fill: true,
              fillColor: '#3b82f6',
              fillOpacity: 0.1,
              maintainColor: true
            }
          },
          remove: true
        }}
      />
    </FeatureGroup>
  );
};

export default function DrawableMap({ 
  listings = [], 
  country = 'LK',
  onAreaChange,
  onListView,
  className = '' 
}) {
  const [drawnPolygon, setDrawnPolygon] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [filteredListings, setFilteredListings] = useState(listings);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapRef = useRef(null);
  
  const currency = country === 'UK' ? '£' : 'Rs.';
  
  // Default center based on country
  const defaultCenter = country === 'UK' 
    ? [51.5074, -0.1278] 
    : [6.9271, 79.8612];
  
  // Filter listings when polygon changes
  useEffect(() => {
    if (drawnPolygon && drawnPolygon.length > 0) {
      const filtered = listings.filter(listing => {
        if (!listing.latitude || !listing.longitude) return false;
        return isPointInPolygon([listing.latitude, listing.longitude], drawnPolygon);
      });
      setFilteredListings(filtered);
      
      if (onAreaChange) {
        onAreaChange({
          polygon: drawnPolygon,
          matchCount: filtered.length,
          listings: filtered
        });
      }
    } else {
      setFilteredListings(listings);
      if (onAreaChange) {
        onAreaChange(null);
      }
    }
  }, [drawnPolygon, listings, onAreaChange]);
  
  const handleDrawCreated = useCallback((e) => {
    const { layer, layerType } = e;
    
    if (layerType === 'polygon') {
      const latLngs = layer.getLatLngs()[0];
      const polygon = latLngs.map(ll => [ll.lat, ll.lng]);
      setDrawnPolygon(polygon);
      setIsDrawing(false);
    }
  }, []);
  
  const handleDrawEdited = useCallback((e) => {
    // Handle polygon editing (resize/reshape)
    const layers = e.layers;
    layers.eachLayer((layer) => {
      if (layer.getLatLngs) {
        const latLngs = layer.getLatLngs()[0];
        const polygon = latLngs.map(ll => [ll.lat, ll.lng]);
        setDrawnPolygon(polygon);
      }
    });
  }, []);
  
  const handleDrawDeleted = useCallback(() => {
    setDrawnPolygon(null);
  }, []);
  
  const clearDrawing = () => {
    setDrawnPolygon(null);
  };
  
  return (
    <div 
      className={`relative bg-gray-100 ${isFullscreen ? 'fixed inset-0 z-50' : 'h-[600px]'} ${className}`}
      data-testid="drawable-map"
    >
      {/* Controls - positioned to not overlap with draw tools */}
      <div className="absolute top-4 left-20 z-[1000] flex flex-col gap-2">
        <Button 
          variant="secondary"
          size="sm"
          onClick={onListView}
          className="shadow-lg bg-white"
          data-testid="switch-to-list-btn"
        >
          <List className="w-4 h-4 mr-2" />
          List View
        </Button>
        
        {drawnPolygon && (
          <Button
            variant="secondary"
            size="sm"
            onClick={clearDrawing}
            className="shadow-lg bg-white text-red-600 hover:bg-red-50"
            data-testid="clear-drawing-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Area
          </Button>
        )}
      </div>
      
      {/* Instructions */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000]">
        {!drawnPolygon ? (
          <div className="bg-white shadow-lg rounded-lg px-4 py-2 flex items-center gap-2">
            <PenTool className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium">
              Click the polygon tool on the left to draw your search area
            </span>
          </div>
        ) : (
          <Badge className="bg-blue-600 text-white shadow-lg px-4 py-2">
            <Search className="w-4 h-4 mr-2" />
            {filteredListings.length} properties in this area
          </Badge>
        )}
      </div>
      
      {/* Fullscreen toggle */}
      <div className="absolute top-4 right-4 z-[1000]">
        <Button
          variant="secondary"
          size="icon"
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="shadow-lg bg-white h-8 w-8"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </Button>
      </div>
      
      {/* Map */}
      <MapContainer
        ref={mapRef}
        center={defaultCenter}
        zoom={12}
        className="h-full w-full"
        zoomControl={false}
        style={{ zIndex: 1 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Draw Controls */}
        <DrawController 
          onDrawCreated={handleDrawCreated}
          onDrawDeleted={handleDrawDeleted}
          onDrawEdited={handleDrawEdited}
        />
        
        {/* Drawn polygon visualization */}
        {drawnPolygon && (
          <Polygon
            positions={drawnPolygon}
            pathOptions={{
              color: '#3b82f6',
              fillColor: '#3b82f6',
              fillOpacity: 0.15,
              weight: 2,
              dashArray: '5, 5'
            }}
          />
        )}
        
        {/* Property Markers */}
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
          maxClusterRadius={60}
          disableClusteringAtZoom={16}
        >
          {filteredListings.map(listing => {
            if (!listing.latitude || !listing.longitude) return null;
            
            return (
              <Marker
                key={listing.id}
                position={[listing.latitude, listing.longitude]}
                icon={createPropertyIcon(listing.price, currency)}
              >
                <Popup>
                  <div className="w-52">
                    <img 
                      src={listing.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=300'}
                      alt={listing.title}
                      className="w-full h-28 object-cover rounded mb-2"
                    />
                    <h4 className="font-semibold text-sm mb-1">{listing.title}</h4>
                    <p className="text-blue-600 font-bold">
                      {currency}{listing.price?.toLocaleString()}/mo
                    </p>
                    <div className="flex gap-2 text-xs text-gray-600 mt-1">
                      {listing.bedrooms !== undefined && (
                        <span>{listing.bedrooms === 0 ? 'Studio' : `${listing.bedrooms} bed`}</span>
                      )}
                      {listing.property_type && <span>• {listing.property_type}</span>}
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full mt-2 h-7 text-xs"
                      onClick={() => window.open(`/listing/${listing.id}`, '_blank')}
                    >
                      View Details
                    </Button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>
      
      {/* Results count overlay */}
      <div className="absolute bottom-4 left-4 z-[1000]">
        <Badge className="bg-white text-gray-800 shadow-lg px-3 py-1.5">
          <MapPin className="w-4 h-4 mr-1" />
          {filteredListings.length} properties {drawnPolygon ? 'in area' : 'found'}
        </Badge>
      </div>
    </div>
  );
}
